import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetProjectQuery } from "../../app/services/projects";
import {
  isClientGroupPosition,
  isPrimaryClientPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
} from "../../utils/memberPositionUtils";
import useProjectDmAccess from "../../hooks/useProjectDmAccess";
import {
  dispatchShortcutAction,
  getAltDigit,
  isTypingTarget,
  type ShortcutAction,
} from "../../utils/keyboardShortcuts";
import { useProjectPanelTabs } from "./useProjectPanelTabs";

/** 基本タブ（⌥3〜⌥6 の割り当て。config はプロジェクトの設定） */
const NAV_TAB_BY_DIGIT: Record<number, NavTab | undefined> = {
  3: "home",
  4: "talkroom",
  5: "dm",
  6: "config",
};

/** 表示中タブへの再押下（⌥3〜⌥6）で発火するページ内循環アクション */
const CYCLE_ACTION_BY_TAB: Record<NavTab, ShortcutAction> = {
  home: "home-cycle",
  talkroom: "talkroom-cycle",
  dm: "dm-cycle",
  config: "config-cycle",
};

/** Escape でプロジェクトリストへ戻る対象タブ */
const ESC_TO_PROJECTS_TABS = new Set(["home", "talkroom", "dm", "config"]);

type NavTab = "home" | "talkroom" | "dm" | "config";

interface UseProjectShortcutsOptions {
  /** ProjectWrapper の handleTabChange（activeTab と ?tab= を同期する） */
  onTabChange: (tab: NavTab | "notes" | "library") => void;
  /** プレビュー（/:projectId/:shotId）表示中か */
  isPreviewRoute: boolean;
  /** ProjectWrapper の activeTab（?tab= と同期している表示中タブ） */
  activeTab: string;
}

/**
 * プロジェクト画面内のキーボードショートカット（⌥3〜⌥9 / Esc）。ProjectWrapper から
 * 呼び出す。一覧はヘッダー「?」の ShortcutGuideDrawer に掲載している。
 *
 * - ⌥3: ホーム / ⌥4: トークルーム / ⌥5: DM / ⌥6: プロジェクトの設定
 *   既に該当タブを表示中の再押下は、ページ内のセグメント / メニューを循環切替する
 *   （dispatchShortcutAction 経由で各ページが処理）
 * - ⌥7: イメージボード ON/OFF（もう一度押すと開く前の画面へ戻る）
 * - ⌥8: ノート / ⌥9: 共有ライブラリ（下部バーのタブを開く。表示条件は下部バーと同じ）
 * - Esc: ホーム / トークルーム / DM / プロジェクトの設定の表示中はプロジェクトリスト
 *   （/projects）へ戻る
 *
 * 権限判定は BottomAppBarProject の表示ナビ・プロジェクトメニューと同一基準:
 * - クライアントグループ: home / talkroom（プロジェクトの設定は責任者 CLIENT のみ。
 *   DM は責任者に加え、参加中スレッドのある REVIEWER も可 = useProjectDmAccess）
 * - プロデューサーグループ: 全タブ
 * - SPECIALIST: DM のみ
 * - 納品物なし（total_shots_count === 0）のプロジェクトはホーム無効
 *
 * プレビュー表示中はタブが描画されないため、`/${projectId}?tab=...` へ遷移して
 * プレビューを閉じた上で該当タブを開く（⌥7 もトグルではなく直接開く）。
 */
export const useProjectShortcuts = ({
  onTabChange,
  isPreviewRoute,
  activeTab,
}: UseProjectShortcutsOptions) => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // 権限判定用データ（ProjectWrapper 等と同一クエリのため RTK Query キャッシュを共有）
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(
    projectId ?? "",
    { skip: !projectId || !currentUserData },
  );
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );

  const positionStr: string | undefined =
    membershipData?.data?.position ?? undefined;
  const noDeliverable = projectData?.data?.project?.total_shots_count === 0;

  // ⌥7: イメージボードの表示条件・トグル遷移（useProjectPanelTabs）。
  // ⌥8/⌥9: 下部バーのノート / ライブラリと同じ表示条件
  const { showImageBoard, showNotes, showLibrary, toggleImageBoard } =
    useProjectPanelTabs();

  // DM タブの可否（BottomAppBarProject の表示ナビと同じ hook を共用）
  const { canAccessDm } = useProjectDmAccess(projectId);

  /** BottomAppBarProject の表示ナビと同じ基準でタブへのアクセス可否を判定する */
  const canAccessTab = useCallback(
    (tab: NavTab): boolean => {
      if (!positionStr) return false;
      if (isSpecialistPosition(positionStr)) return tab === "dm";
      if (isProducerGroupPosition(positionStr)) {
        return tab === "home" ? !noDeliverable : true;
      }
      if (isClientGroupPosition(positionStr)) {
        if (tab === "home") return !noDeliverable;
        if (tab === "talkroom") return true;
        // DM は責任者（CLIENT）に加え、参加中スレッドのある REVIEWER も可
        if (tab === "dm") return canAccessDm;
        // プロジェクトの設定はクライアント責任者（CLIENT）のみ。REVIEWER は不可
        return isPrimaryClientPosition(positionStr);
      }
      return false;
    },
    [positionStr, noDeliverable, canAccessDm],
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!projectId) return;

    // Esc: ホーム / トークルーム / DM / プロジェクトの設定の表示中はプロジェクトリストへ戻る。
    // プレビューの Esc（閉じる）は Preview 側、ドロワー等は各コンポーネントが
    // capture で処理するため、ここではベースタブ表示中のみ反応する。
    if (e.key === "Escape") {
      if (isPreviewRoute) return;
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.repeat || e.isComposing || e.defaultPrevented) return;
      if (isTypingTarget(e.target)) return;
      if (!ESC_TO_PROJECTS_TABS.has(activeTab)) return;
      // モーダル / ダイアログ表示中は、そちらの「Esc で閉じる」を優先する
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      navigate("/projects");
      return;
    }

    const digit = getAltDigit(e);
    if (digit === null || digit < 3) return;
    if (isTypingTarget(e.target)) return;

    // ⌥3〜⌥6: 下部ナビ相当のタブ切替。
    // 既に該当タブを表示中の再押下は、ページ内のセグメント / メニューの循環切替。
    const navTab = NAV_TAB_BY_DIGIT[digit];
    if (navTab) {
      if (!canAccessTab(navTab)) return;
      e.preventDefault();
      if (isPreviewRoute) {
        navigate(
          navTab === "home" ? `/${projectId}` : `/${projectId}?tab=${navTab}`,
        );
      } else if (activeTab === navTab) {
        dispatchShortcutAction(CYCLE_ACTION_BY_TAB[navTab]);
      } else {
        onTabChange(navTab);
      }
      return;
    }

    // ⌥7: イメージボード（プロジェクトのメインのボード）の ON/OFF トグル。
    // プレビューからは旧 URL で開き、ProjectWrapper がノートタブのボードへ振り替える
    if (digit === 7) {
      if (!showImageBoard) return;
      e.preventDefault();
      if (isPreviewRoute) {
        navigate(`/${projectId}?tab=imageboard`);
      } else {
        void toggleImageBoard();
      }
      return;
    }

    // ⌥8/⌥9: ノート / 共有ライブラリのタブを開く
    if (digit === 8 || digit === 9) {
      const tab = digit === 8 ? "notes" : "library";
      const available = digit === 8 ? showNotes : showLibrary;
      if (!available) return;
      e.preventDefault();
      if (isPreviewRoute) {
        navigate(`/${projectId}?tab=${tab}`);
      } else if (activeTab !== tab) {
        onTabChange(tab);
      }
    }
  };

  // ProjectWrapper はスクロール等で頻繁に再レンダリングされるため、最新ハンドラを
  // ref 経由で参照し、リスナーの登録はマウント時の 1 回に抑える。
  const handlerRef = useRef(handleKeyDown);
  useEffect(() => {
    handlerRef.current = handleKeyDown;
  });

  useEffect(() => {
    const listener = (e: KeyboardEvent) => handlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);
};

export default useProjectShortcuts;
